import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/context/AuthContext';
import { UnAuthenticatedNavbar } from './UnAuthenticatedNavbar';
import { AuthenticatedNavbar } from './AuthenticatedNavbar';
import {
  DASHBOARD,
  SUBSCRIPTION_PAGE,
  SUPER_ADMIN_LEGAL_DOCS,
} from 'utilities/routes';
import { Roles } from '../../firebase/interfaces';
import { ProviderAuthenticatedNavbar } from './ProviderAuthenticatedNavbar';
import { ProviderAuthenticatedNotApprovedNavbar } from 'components/navbar/ProviderAuthenticatedNotApprovedNavbar';
import { SuperAdminAuthenticatedNavbar } from 'components/navbar/SuperAdminAuthenticatedNavbar';
import JoinToAppointmentOutlet from './JoinToAppointmentOutlet';
import { useVerifyChild } from '../../app/my-account/pages/child/useVerifyChild';
import UnverifiedChildNavbar from './UnverifiedChildNavbar';
import { SUBSCRIPTION_ACTIVE_STATUS } from 'auth/context/AuthProvider';
import ExpiringSubscriptionOutlet from './ExpiringSubscriptionOutlet';
import { twMerge } from 'tailwind-merge';

import { ReactComponent as LogoIcon } from 'assets/icons/sgbnet.svg';
import { ReactComponent as AdminLogoIcon } from 'assets/icons/admin-logo.svg';
import UpdateLisences from './UpdateLisences';
import { ADMIN_ROLES } from '../../utilities/constants';
import ScheduledDnaTestOutlet from './ScheduledDnaTestOutlet';
import { usePatientProfileContext } from '../../app/my-account/pages/patient/PatientProfileContext';
import { ChildGenderEnum } from '../../app/my-account/pages/add-child/useCreateSubPatient';

const childHeaderColorClasses = new Map<string, string>([
  [ChildGenderEnum.MALE, 'from-[#92E4FA80]'],
  [ChildGenderEnum.FEMALE, 'from-[#FFCEFFFF]'],
]);

interface NavbarProps {
  openNavbarMobile: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ openNavbarMobile }) => {
  const {
    isLoggedIn,
    user,
    selectedAccount,
    stripeSubscription,
    hasActivePeriod,
  } = useAuth();
  const { profileData } = usePatientProfileContext();

  const { isVerify } = useVerifyChild();
  const navigate = useNavigate();
  const userRole = useMemo(() => user?.role, [user]);

  const isAdmin = !!userRole && ADMIN_ROLES.includes(userRole);

  const Logo = isAdmin ? AdminLogoIcon : LogoIcon;
  const logoLink = isAdmin ? SUPER_ADMIN_LEGAL_DOCS : DASHBOARD;

  const location = useLocation();
  const isShowSubExpiredBanner = useMemo(
    () =>
      !!stripeSubscription?.length &&
      !SUBSCRIPTION_ACTIVE_STATUS.includes(
        stripeSubscription?.[0]?.status as number,
      ) &&
      location.pathname !== SUBSCRIPTION_PAGE &&
      userRole === Roles.PROVIDER,
    [location, stripeSubscription, userRole],
  );

  const isLicensesShow = useMemo(() => userRole === Roles.PROVIDER, [userRole]);

  const getAuthenticatedNavbarBasedOnUserRole = useCallback(() => {
    switch (userRole) {
      case Roles.PATIENT:
        if (!isVerify && selectedAccount) {
          return <UnverifiedChildNavbar openNavbarMobile={openNavbarMobile} />;
        }
        return <AuthenticatedNavbar openNavbarMobile={openNavbarMobile} />;
      case Roles.PROVIDER:
        if (
          user?.providerEnabled &&
          stripeSubscription?.length &&
          hasActivePeriod
        ) {
          return (
            <ProviderAuthenticatedNavbar openNavbarMobile={openNavbarMobile} />
          );
        }
        return (
          <ProviderAuthenticatedNotApprovedNavbar
            openNavbarMobile={openNavbarMobile}
          />
        );
      case Roles.SUPERADMIN:
      case Roles.DELEGATED_ADMIN:
        return (
          <SuperAdminAuthenticatedNavbar openNavbarMobile={openNavbarMobile} />
        );
      default:
        return <AuthenticatedNavbar openNavbarMobile={openNavbarMobile} />;
    }
  }, [
    userRole,
    isVerify,
    selectedAccount,
    openNavbarMobile,
    user,
    stripeSubscription,
    hasActivePeriod,
  ]);

  return (
    <div
      className={
        'w-full z-[999] relative desktop:min-w-[1300px] box-border bg-white border-b shrink-0 p-0'
      }
    >
      {!isAdmin && <div className="w-full h-2.5 bg-clc-blue" />}
      <div
        className={twMerge(
          'w-full h-[70px] desktop:h-[90px] px-7 2xl:px-[135px] flex justify-between items-center gap-4',
          isAdmin && 'bg-header',
          selectedAccount &&
            userRole === Roles.PATIENT &&
            `bg-gradient-to-b ${childHeaderColorClasses.get(
              profileData?.gender as string,
            )} via-50% to-white to-50%`,
        )}
        data-testid="navbar"
      >
        <div className="flex items-center gap-2.5">
          <Logo
            className="w-[117px] h-[27px] desktop:w-[162px] desktop:h-9 cursor-pointer"
            onClick={() => navigate(logoLink)}
          />
          {isAdmin && (
            <div className="flex border-l border-[#848484] items-center pl-2.5 h-14 font-semibold text-white">
              Admin
            </div>
          )}
        </div>

        {!isLoggedIn && <UnAuthenticatedNavbar />}
        {isLoggedIn && (
          <div className="ml-auto">
            {getAuthenticatedNavbarBasedOnUserRole()}
          </div>
        )}
      </div>
      {isShowSubExpiredBanner && <ExpiringSubscriptionOutlet />}
      {isLicensesShow && <UpdateLisences />}
      <JoinToAppointmentOutlet />
      <ScheduledDnaTestOutlet />
    </div>
  );
};
export default Navbar;
